/**
 * resumeConstants.js
 * Single source of truth for all static resume builder data.
 * Extracted from CompleteEnhancedResumeBuilder to eliminate re-creation on render.
 */

import {
  User, Target, Layers, Briefcase, GraduationCap, Download,
  BookOpen, Github, Users, ExternalLink, Lightbulb, Globe, Heart, Award,
} from 'lucide-react';

// ── Core 6-step flow ──────────────────────────────────────────────────────────
export const STEPS = [
  { id: 'personal',           title: 'Identity',              icon: User,          phase: 1 },
  { id: 'summary',            title: 'Your Story',            icon: Target,        phase: 1 },
  { id: 'skills-expertise',   title: 'Skills & Expertise',    icon: Layers,        phase: 2 },
  { id: 'experience-projects',title: 'Experience & Projects', icon: Briefcase,     phase: 2 },
  { id: 'credentials',        title: 'Credentials',           icon: GraduationCap, phase: 3 },
  { id: 'review',             title: 'ATS Score & Download',  icon: Download,      phase: 3 },
];

// ── Optional sections — hidden by default, user opts in via Credentials step ──
export const OPTIONAL_SECTIONS = [
  { id: 'awards',        title: 'Awards & Recognition',       icon: Award,         who: 'Recommended for 3+ years experience' },
  { id: 'languages',     title: 'Languages',                  icon: Globe,         who: 'Add if you speak 2+ languages' },
  { id: 'volunteer',     title: 'Volunteer Work',             icon: Heart,         who: 'Great for community-focused roles' },
  { id: 'publications',  title: 'Publications & Talks',       icon: BookOpen,      who: 'Recommended for Senior / Lead QA' },
  { id: 'opensource',    title: 'Open Source Contributions',  icon: Github,        who: 'Recommended for Automation / SDET roles' },
  { id: 'memberships',   title: 'Professional Memberships',   icon: Users,         who: 'ISTQB, ASQ, or similar bodies' },
  { id: 'portfolio',     title: 'Portfolio & Case Studies',   icon: ExternalLink,  who: 'Recommended for QA Lead / Consultant' },
  { id: 'patents',       title: 'Patents & Innovations',      icon: Lightbulb,     who: 'Only if you hold or filed a patent' },
];

// ── Skill categories ──────────────────────────────────────────────────────────
export const SKILL_CATEGORIES = {
  testing:     ['Manual Testing', 'Functional Testing', 'Regression Testing', 'API Testing', 'Performance Testing', 'Security Testing', 'Usability Testing', 'Accessibility Testing', 'UAT', 'Integration Testing', 'System Testing', 'Smoke Testing', 'Sanity Testing', 'Exploratory Testing', 'Ad-hoc Testing', 'Compatibility Testing', 'End-to-End Testing', 'E2E Testing', 'Contract Testing', 'Risk-based Testing', 'ETL Testing', 'Backend Testing', 'Batch Job Testing'],
  automation:  ['Selenium WebDriver', 'Playwright', 'Cypress', 'TestComplete', 'Appium', 'Robot Framework', 'Katalon Studio', 'Puppeteer', 'Cucumber', 'RestAssured', 'JUnit', 'TestNG', 'PyTest', 'Jest', 'Mocha', 'Chai', 'WebdriverIO', 'Espresso', 'XCUITest', 'Detox', 'Karate', 'SoapUI', 'SpecFlow', 'Behave'],
  programming: ['Java', 'Python', 'JavaScript', 'C#', 'TypeScript', 'SQL', 'Bash/Shell', 'PowerShell', 'C++', 'Ruby', 'Go', 'Kotlin', 'Swift', 'PHP', 'HTML5', 'CSS3'],
  databases:   ['MySQL', 'PostgreSQL', 'MongoDB', 'Oracle', 'SQL Server', 'Redis', 'Cassandra', 'DynamoDB', 'SQLite', 'MariaDB', 'Firebase', 'Elasticsearch', 'GraphQL'],
  cloud:       ['AWS', 'Azure', 'GCP', 'BrowserStack', 'Sauce Labs', 'LambdaTest', 'AWS Device Farm', 'Perfecto', 'Firebase Test Lab', 'Docker', 'Kubernetes', 'Terraform', 'Ansible'],
  aiTools:     ['GitHub Copilot', 'Cursor', 'Windsurf', 'ChatGPT', 'Testim', 'Mabl', 'Applitools', 'Functionize', 'Prompt Engineering', 'AI Test Generation', 'LLM Testing', 'LangChain', 'Ragas', 'Deepeval', 'Promptfoo'],
};

export const SKILL_CATEGORY_TITLES = {
  testing:     'Testing Methodologies',
  automation:  'Automation Frameworks',
  programming: 'Programming Languages',
  databases:   'Database Technologies',
  cloud:       'Cloud & Testing Platforms',
  aiTools:     'AI & ML Tools',
};

// ── Tool categories ───────────────────────────────────────────────────────────
export const TOOL_CATEGORIES = {
  testManagement: ['JIRA', 'TestRail', 'Zephyr', 'qTest', 'PractiTest', 'TestLink', 'Azure Test Plans', 'Xray', 'Linear', 'Notion', 'Confluence', 'ALM/Quality Center', 'Trello'],
  automation:     ['Jenkins', 'GitLab CI', 'GitHub Actions', 'Azure DevOps', 'TeamCity', 'Bamboo', 'CircleCI', 'Travis CI', 'Bitrise', 'Maven', 'Gradle', 'npm', 'Git'],
  cicd:           ['Docker', 'Kubernetes', 'AWS', 'Azure', 'GCP', 'Terraform', 'Ansible', 'CloudFormation', 'Helm', 'Vagrant'],
  monitoring:     ['Grafana', 'Prometheus', 'New Relic', 'Datadog', 'Splunk', 'ELK Stack', 'Kibana', 'Logstash', 'Dynatrace', 'AppDynamics'],
};

export const TOOL_CATEGORY_TITLES = {
  testManagement: 'Test Management Tools',
  automation:     'CI/CD & Automation',
  cicd:           'Cloud & Infrastructure',
  monitoring:     'Monitoring & Analytics',
};

// ── Core competency options ───────────────────────────────────────────────────
export const CORE_COMPETENCY_OPTIONS = [
  'Test Strategy & Planning', 'Quality Assurance Leadership', 'Cross-functional Collaboration',
  'Process Improvement', 'Risk Assessment', 'Agile/Scrum Methodologies', 'DevOps Integration',
  'Test Automation Strategy', 'Performance Optimization', 'Security Testing', 'Mobile Testing',
  'API Testing', 'Database Testing', 'CI/CD Pipeline Testing', 'Defect Management',
];

// ── Default form shape ────────────────────────────────────────────────────────
export const uid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);

export const makeDefaultFormData = (userEmail = '') => ({
  personal: { name: '', jobTitle: 'QA Engineer', targetHeadline: 'Senior SDET | AI Automation Lead | MCP Specialist', email: userEmail, phone: '', location: '', linkedin: '', github: '', portfolio: '' },
  summary: '',
  coreCompetencies: [],
  technicalSkills: { testing: [], automation: [], programming: [], databases: [], cloud: [], aiTools: [] },
  tools: { testManagement: [], automation: [], cicd: [], monitoring: [] },
  experience:    [{ _id: uid(), company: '', role: '', duration: '', location: '', technologies: '', impact: '', achievements: [{ _id: uid(), text: '' }] }],
  projects:      [{ _id: uid(), title: '', domain: '', description: '', responsibilities: [{ _id: uid(), text: '' }], technologies: '', impact: '' }],
  achievements:  [{ _id: uid(), title: '', organization: '', year: '', description: '' }],
  certifications:[{ _id: uid(), name: '', organization: '', year: '', credentialId: '' }],
  education:     [{ _id: uid(), degree: '', university: '', year: '', gpa: '' }],
  awards:        [{ _id: uid(), title: '', organization: '', year: '', description: '' }],
  languages:     [{ _id: uid(), language: '', proficiency: 'Professional' }],
  volunteer:     [{ _id: uid(), organization: '', role: '', duration: '', description: '' }],
  publications:  [{ _id: uid(), title: '', publication: '', date: '', url: '', type: 'Article' }],
  openSource:    [{ _id: uid(), project: '', description: '', role: '', technologies: '', url: '', stars: '' }],
  memberships:   [{ _id: uid(), organization: '', role: '', since: '', level: '' }],
  portfolio:     [{ _id: uid(), title: '', description: '', url: '', type: 'Case Study', technologies: '' }],
  patents:       [{ _id: uid(), title: '', patentNumber: '', status: 'Pending', date: '', description: '' }],
  aiInjectedKeywords: [],
});
